/**
 * FormPanel Component
 *
 * Renders a multi-field form for player input.
 * Supports text_input, textarea, radio, and checkbox field types.
 * Handles validation, timeout countdown, and submit.
 */

import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import type {
  FormPanelDefinition,
  FormFieldDefinition,
  TextInputFieldDef,
  AITextInputFieldDef,
  TextAreaFieldDef,
  RadioFieldDef,
  SliderFieldDef,
  CheckboxFieldDef,
  ActionGridFieldDef,
} from "@/types/actions"
import { HapticFeedbackService } from "@/services/hapticFeedback"
import { ActionGrid } from "@/components/medium/ActionGrid"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Progress } from "@/components/ui/progress"
import { AITextInput } from "@/components/medium/AITextInput"
import { EmojiText } from '../../components/low/AnimatedEmoji'
import { useQuestionTimer } from "@/hooks/useQuestionTimer"

interface FormPanelProps {
  panel: FormPanelDefinition
  /** Resets the countdown between rounds — panel IDs are reused by game modes. */
  requestId: string
  onSubmit: (fieldValues: Record<string, string | string[]>) => void
  /**
   * Answers the whole panel with a single action ID. Required by `action_grid`
   * fields, whose cell click submits immediately and closes the barrier —
   * anything typed into sibling fields is lost, the form submit never happens.
   * Optional so forms without action grids keep their current call sites.
   *
   * Must reject on a failed submit: the resolved promise is the only success
   * signal the panel has, and it drives the haptic feedback.
   */
  onActionClick?: (actionId: string) => void | Promise<void>
  onTimeout?: () => void
  disabled?: boolean
}

type FieldValues = Record<string, string | string[]>

function initFieldValues(panel: FormPanelDefinition): FieldValues {
  const values: FieldValues = {}
  for (const field of panel.fields) {
    // action_grid carries no value — it answers by click, not by submit.
    if (field.field_type === "action_grid") {
      continue
    }
    // Only checkbox holds a list; every other type (incl. ai_text_input) starts as "".
    if (field.field_type === "checkbox") {
      values[field.id] = []
    } else {
      values[field.id] = ""
    }
  }
  return values
}

function isFieldRequired(field: FormFieldDefinition): boolean {
  switch (field.field_type) {
    case "text_input":
    case "ai_text_input":
    case "textarea":
      return (field as TextInputFieldDef | AITextInputFieldDef | TextAreaFieldDef).required !== false
    case "radio":
      return (field as RadioFieldDef).required !== false
    case "slider":
      return (field as SliderFieldDef).required !== false
    case "checkbox":
      // Pydantic serializes default 0, so min_selections is always defined at runtime.
      // TypeScript type has `?` — use nullish coalescing to satisfy type checker.
      return ((field as CheckboxFieldDef).min_selections ?? 0) > 0
    case "action_grid":
      // Carries no value, so it can never be "filled" — stays out of the progress count.
      return false
    default:
      return true
  }
}

function isFieldValid(field: FormFieldDefinition, value: string | string[]): boolean {
  switch (field.field_type) {
    case "text_input":
    case "ai_text_input":
    case "textarea": {
      const required = (field as TextInputFieldDef | AITextInputFieldDef | TextAreaFieldDef).required !== false
      if (required) return typeof value === "string" && value.trim().length > 0
      return true
    }
    case "radio": {
      const required = (field as RadioFieldDef).required !== false
      if (required) return typeof value === "string" && value.length > 0
      return true
    }
    case "slider": {
      const required = (field as SliderFieldDef).required !== false
      if (required) return typeof value === "string" && value.length > 0
      return true
    }
    case "checkbox": {
      const f = field as CheckboxFieldDef
      const selected = Array.isArray(value) ? value.length : 0
      const minOk = f.min_selections === undefined || selected >= f.min_selections
      // `== null` on purpose: an unlimited field arrives as null, not undefined,
      // and `selected <= null` would compare against 0 and reject every selection.
      const maxOk = f.max_selections == null || selected <= f.max_selections
      return minOk && maxOk
    }
    case "action_grid":
      // Carries no value — never blocks the submit button of the sibling fields.
      return true
    default: {
      const exhaustiveCheck: never = field
      console.error('[FormPanel] Unknown field_type:', (exhaustiveCheck as FormFieldDefinition).field_type)
      return false
    }
  }
}

/**
 * FormPanel - Renders a multi-field form for player input.
 */
export const FormPanel: React.FC<FormPanelProps> = ({
  panel,
  requestId,
  onSubmit,
  onActionClick,
  onTimeout,
  disabled = false,
}) => {
  const { t } = useTranslation()
  const [fieldValues, setFieldValues] = useState<FieldValues>(() => initFieldValues(panel))
  const { timeLeft, timedOut } = useQuestionTimer(panel.timeout_seconds, requestId)
  // IDs of ai_text_input fields waiting for backend suggestions. The backend holds the
  // session lock during the LLM call, so submitting now would block without feedback.
  // Tracked per field so two AI fields cannot clear each other's flag.
  const [aiLoadingFieldIds, setAiLoadingFieldIds] = useState<string[]>([])
  const aiSuggestLoading = aiLoadingFieldIds.length > 0

  const setAiFieldLoading = (fieldId: string, loading: boolean) => {
    setAiLoadingFieldIds((prev) => {
      if (loading) return prev.includes(fieldId) ? prev : [...prev, fieldId]
      return prev.filter((id) => id !== fieldId)
    })
  }

  // After timeout, call onTimeout after 1s
  useEffect(() => {
    if (!timedOut) return
    const t = setTimeout(() => {
      onTimeout?.()
    }, 1000)
    return () => clearTimeout(t)
  }, [timedOut, onTimeout])

  // Action ID of the grid cell being submitted. One flag for the whole panel:
  // two quick taps in different grids would fire two requests, and the second
  // fails with 404 (the single-player barrier is already completed) — an error
  // shown to the player despite a correctly recorded answer.
  const [pendingActionId, setPendingActionId] = useState<string | null>(null)

  const isDisabled = disabled || timedOut

  const handleActionClick = async (actionId: string) => {
    if (isDisabled || pendingActionId !== null || !onActionClick) return

    setPendingActionId(actionId)
    HapticFeedbackService.notification()

    try {
      await onActionClick(actionId)
      HapticFeedbackService.success()
    } catch (err) {
      console.error("[FormPanel] Error in action click:", err)
      HapticFeedbackService.error()
    } finally {
      setPendingActionId(null)
    }
  }

  const hasActionGrid = panel.fields.some((field) => field.field_type === "action_grid")
  // No submit button means the panel answers by grid click alone. One criterion for
  // the button, the progress indicator and the submit lock — they describe the same
  // "this form can be submitted" fact.
  const hasSubmit = Boolean(panel.submit_label)

  // An action_grid cannot be answered without this prop, and such a panel has no
  // submit button to fall back on — the game would stall on a panel the player
  // cannot answer. Loud once per panel, not on every render.
  useEffect(() => {
    if (hasActionGrid && !onActionClick) {
      console.error(
        "[FormPanel] panel contains an action_grid field but no onActionClick prop — cells are inert",
      )
    }
  }, [hasActionGrid, onActionClick])

  const isValid = panel.fields.every((field) =>
    isFieldValid(field, fieldValues[field.id] ?? (field.field_type === "checkbox" ? [] : ""))
  )

  const handleSubmit = () => {
    // pendingActionId: a grid cell already answered this barrier. Submitting now
    // would fire a second request at a request_id that is closing, and the player
    // would see an error over a correctly recorded answer.
    if (!isValid || isDisabled || aiSuggestLoading || pendingActionId !== null) return
    onSubmit(fieldValues)
  }

  const setFieldValue = (fieldId: string, value: string | string[]) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }))
  }

  const renderField = (field: FormFieldDefinition) => {
    const value = fieldValues[field.id]

    switch (field.field_type) {
      case "text_input": {
        const f = field as TextInputFieldDef
        return (
          <div key={field.id} className="flex flex-col gap-1.5">
            <Label htmlFor={field.id} className="text-sm font-semibold">{/* AC1 font-semibold (was font-medium) */}
              {field.label}
            </Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
            <Input
              id={field.id}
              placeholder={f.placeholder}
              maxLength={f.max_length}
              value={typeof value === "string" ? value : ""}
              onChange={(e) => setFieldValue(field.id, e.target.value)}
              disabled={isDisabled}
              className="min-h-[44px]"
            />
          </div>
        )
      }

      case "ai_text_input": {
        const f = field as AITextInputFieldDef
        return (
          <AITextInput
            key={field.id}
            field={f}
            value={typeof value === "string" ? value : ""}
            onChange={(v) => setFieldValue(field.id, v)}
            disabled={isDisabled}
            onLoadingChange={(loading) => setAiFieldLoading(field.id, loading)}
          />
        )
      }

      case "textarea": {
        const f = field as TextAreaFieldDef
        return (
          <div key={field.id} className="flex flex-col gap-1.5">
            <Label htmlFor={field.id} className="text-sm font-semibold">{/* AC1 font-semibold (was font-medium) */}
              {field.label}
            </Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
            <Textarea
              id={field.id}
              placeholder={f.placeholder}
              maxLength={f.max_length}
              rows={f.rows}
              value={typeof value === "string" ? value : ""}
              onChange={(e) => setFieldValue(field.id, e.target.value)}
              disabled={isDisabled}
            />
          </div>
        )
      }

      case "radio": {
        const f = field as RadioFieldDef
        return (
          <div key={field.id} className="flex flex-col gap-1.5" data-testid={`form-field-${field.id}`}>
            <Label className="text-sm font-semibold">{/* AC1 font-semibold (was font-medium) */}{field.label}</Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
            <RadioGroup
              value={typeof value === "string" ? value : ""}
              onValueChange={(v: string) => setFieldValue(field.id, v)}
              disabled={isDisabled}
            >
              {f.options.map((option) => (
                <div
                  key={option.id}
                  className={`flex items-start gap-3 px-3 py-2 rounded-md bg-card/50 border border-border/30 transition-colors min-h-[44px] ${value !== undefined && value !== null && value === option.id ? "border-primary/50 bg-primary/5" : ""}`}
                >
                  <RadioGroupItem
                    value={option.id}
                    id={`${field.id}-${option.id}`}
                    disabled={isDisabled || option.disabled}
                    className="mt-1"
                  />
                  <Label
                    htmlFor={`${field.id}-${option.id}`}
                    className="text-sm cursor-pointer flex-1 flex flex-col"
                  >
                    <span className="flex items-center gap-1.5">
                      {option.icon && <span aria-hidden="true" className="text-base"><EmojiText>{option.icon}</EmojiText></span>}
                      {option.label}
                    </span>
                    {option.description && <span className="text-[10px] text-muted-foreground max-w-sm">{option.description}</span>}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        )
      }

      case "slider": {
        const f = field as SliderFieldDef
        const selectedValue = typeof value === "string" ? value : ""
        return (
          <div key={field.id} className="flex flex-col gap-1.5" data-testid={`form-field-${field.id}`}>
            <Label className="text-sm font-semibold">{field.label}</Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
            <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={field.label}>
              {f.options.map((option) => {
                const isSelected = selectedValue === option.id
                const isSpecial = option.id === "0"
                let btnClass = "px-3 py-1.5 rounded-full text-sm font-medium transition-colors border "
                if (isSelected && isSpecial) {
                  btnClass += "bg-amber-600 text-white border-amber-500"
                } else if (isSelected) {
                  btnClass += "bg-primary text-primary-foreground border-primary"
                } else {
                  btnClass += "bg-card/50 text-foreground border-border/30 hover:border-primary/40"
                }
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    className={btnClass}
                    disabled={isDisabled || option.disabled}
                    title={option.description ?? undefined}
                    onClick={() => setFieldValue(field.id, option.id)}
                  >
                    {option.icon && <span className="mr-1"><EmojiText>{option.icon}</EmojiText></span>}
                    {option.label}
                  </button>
                )
              })}
            </div>
          </div>
        )
      }

      case "checkbox": {
        const f = field as CheckboxFieldDef
        const selected = Array.isArray(value) ? value : ([] as string[])
        return (
          <div key={field.id} className="flex flex-col gap-1.5" data-testid={`form-field-${field.id}`}>
            <Label className="text-sm font-semibold">{/* AC1 font-semibold (was font-medium) */}{field.label}</Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
            {f.options.map((option) => {
              const isChecked = (selected || []).includes(option.id)
              // `!= null` on purpose: an unlimited field arrives as null, and
              // `length >= null` compares against 0, disabling every option.
              const atMax =
                f.max_selections != null &&
                selected.length >= f.max_selections &&
                !isChecked
              return (
                <div
                  key={option.id}
                  className={`flex items-start gap-3 px-3 py-2 rounded-md bg-card/50 border border-border/30 transition-colors min-h-[44px] ${isChecked ? "border-primary/50 bg-primary/5" : ""}`}
                >
                  <Checkbox
                    id={`${field.id}-${option.id}`}
                    checked={isChecked}
                    disabled={isDisabled || option.disabled || atMax}
                    className="mt-1"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFieldValue(field.id, [...selected, option.id])
                      } else {
                        setFieldValue(field.id, selected.filter((id) => id !== option.id))
                      }
                    }}
                  />
                  <Label
                    htmlFor={`${field.id}-${option.id}`}
                    className="text-sm cursor-pointer flex-1 flex flex-col"
                  >
                    <span className="flex items-center gap-1.5">
                      {option.icon && <span aria-hidden="true" className="text-base"><EmojiText>{option.icon}</EmojiText></span>}
                      {option.label}
                    </span>
                    {option.description && <span className="text-[10px] text-muted-foreground max-w-sm">{option.description}</span>}
                  </Label>
                </div>
              )
            })}
          </div>
        )
      }

      case "action_grid": {
        const f = field as ActionGridFieldDef
        return (
          <div key={field.id} className="flex flex-col gap-1.5">
            <Label className="text-sm font-semibold">{field.label}</Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
            <ActionGrid
              field={f}
              onActionClick={handleActionClick}
              // Without the callback the cells answer nothing — render them dead
              // rather than let the player tap a button that cannot reply.
              disabled={isDisabled || pendingActionId !== null || !onActionClick}
              pendingActionId={pendingActionId}
            />
          </div>
        )
      }

      default: {
        const exhaustiveCheck: never = field
        console.error(
          '[FormPanel] Unknown field_type in renderField:',
          (exhaustiveCheck as FormFieldDefinition).field_type,
        )
        return null
      }
    }
  }

  return (
    <Card
      className="border-t-2 border-primary overflow-hidden flex flex-col min-h-0"
      data-testid="form-panel"
    >
      {/* The card itself must not scroll — its border and rounded corners have to
          stay in view; everything inside it scrolls as one area instead. */}
      <div className="p-6 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Header */}
        <div className="mb-4 flex justify-between items-end">
          <div>
            {panel.title && (
              <h2 className="text-sm font-semibold text-primary mb-1">{panel.title}</h2>
            )}
            {panel.description && (
              <p className="text-muted-foreground text-xs leading-relaxed max-w-[80%]">
                {panel.description}
              </p>
            )}
            {/* Progress indicator — it counts progress towards a submit, so it is
                pointless without one. A grid-only form would otherwise read
                "all fields optional" above a panel whose click is mandatory. */}
            {panel.show_progress !== false && hasSubmit && panel.fields.length > 0 && (() => {
              const requiredFields = panel.fields.filter(isFieldRequired)
              const filledCount = requiredFields.filter(
                (f) => isFieldValid(f, fieldValues[f.id] ?? (f.field_type === "checkbox" ? [] : ""))
              ).length
              return (
                <div className="mb-2 mt-2" data-testid="form-progress">
                  {requiredFields.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t('form.allOptional')}</p>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground mb-1">
                        {t('form.filledCount', { filled: filledCount, total: requiredFields.length })}
                      </p>
                      {/* Safe: block only renders when requiredFields.length > 0 (guard above) */}
                      <Progress
                        value={(filledCount / requiredFields.length) * 100}
                        className="h-1.5"
                        aria-label={t('form.filledCountAriaLabel', { filled: filledCount, total: requiredFields.length })}
                      />
                    </>
                  )}
                </div>
              )
            })()}
          </div>

          {/* Timer */}
          {timeLeft !== null && timeLeft >= 0 && (
            <div
              className="flex items-center gap-2 px-2 py-1 bg-muted rounded border border-border"
              data-testid="form-timer"
            >
              <div className="w-1.5 h-1.5 bg-secondary rounded-full animate-ping" />
              <span className="text-[8px] text-muted-foreground font-bold">{timeLeft}s</span>
            </div>
          )}
        </div>

        {/* Fields */}
        <div className="flex flex-col gap-5">
          {panel.fields.map((field, index) => {
            const rendered = renderField(field)
            if (!rendered) return null
            return (
              <React.Fragment key={field.id}>
                {index > 0 && <div className="border-t border-border/20 my-1" />}
                {rendered}
              </React.Fragment>
            )
          })}
        </div>

        {/* Submit — absent when the panel answers by grid click. */}
        {panel.submit_label && (
          <Button
            onClick={handleSubmit}
            variant="default"
            size="lg"
            className="w-full min-h-[44px] mt-4"
            disabled={!isValid || isDisabled || aiSuggestLoading || pendingActionId !== null}
            data-testid="form-submit"
          >
            {panel.submit_label}
          </Button>
        )}
      </div>
    </Card>
  )
}
